<template>
	<app-page>
		<app-heading :title="isNew ? 'New Realtor' : realtor.name" />
		<app-form @submit="onSubmit">
			<app-section class="mt-4">
				<app-section-actions>
					<app-button type="submit" small color="success" v-if="isNew">Create</app-button>
					<app-button type="submit" small color="primary" v-else>Save</app-button>
					<app-button type="cancel" small :to="{ name: 'market-realtors' }">Cancel</app-button>
				</app-section-actions>
				<div class="title">Basic informations</div>
				<app-row>
					<app-cell cols="12" md="3">
						<input-text
							type="name"
							label="Name"
							v-model="realtor.name"
							:errors="errors.name"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							type="email"
							label="Email"
							v-model="realtor.email"
							:errors="errors.email"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-text
							label="Brokerage"
							v-model="realtor.brokerage"
							:errors="errors.brokerage"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" md="4">
						<input-text
							type="phone"
							label="Phone"
							v-model="realtor.phone"
							:errors="errors.phone"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-text
							type="phone"
							label="Office phone"
							v-model="realtor.office_phone"
							:errors="errors.office_phone"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-text
							type="number"
							min="0"
							label="Years of experience"
							v-model="realtor.years_experience"
							:errors="errors.years_experience"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12">
						<input-text
							type="url"
							label="Avatar/Profile picture"
							v-model="realtor.avatar"
							:errors="errors.avatar"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<div class="title">Email Subscription</div>
				<app-row>
					<app-cell cols="12" md="4">
						<input-switch
							label="Is opt-in to our email list?"
							v-model="realtor.subscribed"
							:errors="errors.subscribed"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<app-row>
					<app-cell class="pl-0 ml-0">
						<span class="title">Aliases</span>
						<span>
							<app-button
								small
								outlined
								color="success"
								@click="onAddResource('aliases')"
							>
								Add Alias
							</app-button>
						</span>
					</app-cell>
				</app-row>
				<app-data-table
					:items="aliases"
					:headers="aliasesHeader"
					:total="aliases.length"
					:dynamic="false"
					sort-by="alias"
				>
					<template v-slot:actions="{ item }">
						<app-button
							table
							icon="fa-pencil-alt"
							color="secondary"
							@click="onEditResource('aliases', item)"
						>
							Edit
						</app-button>
						<app-button-confirm
							v-if="$ravello.can('marketmath.delete')"
							color="error"
							icon="fa-trash"
							table
							label-confirm="Remove"
							:description="`Are you sure you want to remove alias <b>${item.value}</b>?`"
							title="Remove Alias"
							@click="onDeleteResource('aliases', item.id)"
						>
							Remove
						</app-button-confirm>
					</template>
				</app-data-table>
				<app-divider class="ma-4" />
				<app-row>
					<app-cell class="pl-0 ml-0">
						<span class="title">Alternate Emails</span>
						<span>
							<app-button
								small
								outlined
								color="success"
								@click="onAddResource('email_alt')"
							>
								Add Alternate Email
							</app-button>
						</span>
					</app-cell>
				</app-row>
				<app-data-table
					:items="email_alt"
					:headers="altEmailsHeader"
					:total="email_alt.length"
					:dynamic="false"
					sort-by="alias"
				>
					<template v-slot:actions="{ item }">
						<app-button
							table
							icon="fa-pencil-alt"
							color="secondary"
							@click="onEditResource('email_alt', item)"
						>
							Edit
						</app-button>
						<app-button-confirm
							v-if="$ravello.can('marketmath.delete')"
							color="error"
							icon="fa-trash"
							table
							label-confirm="Remove"
							:description="`Are you sure you want to remove alternate email <b>${item.value}</b>?`"
							title="Remove Alternate Email"
							@click="onDeleteResource('email_alt', item.id)"
						>
							Remove
						</app-button-confirm>
					</template>
				</app-data-table>
			</app-section>
		</app-form>

		<!-- Alias add/edit dialog -->
		<app-dialog
			v-model="dialog.aliases.show"
			max-width="320"
			:title="dialog.aliases.new ? 'New Alias' : 'Update Alias'"
		>
			<app-dialog-body>
				<input-text v-model="dialog.aliases.value" label="Alias" />
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					:color="dialog.aliases.new ? 'success' : 'primary'"
					@click="editResource('aliases', 'Alias')"
				>
					{{ dialog.aliases.new ? 'Add' : 'Update' }}
				</app-button>
				<app-button @click="dialog.aliases.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>

		<!-- Alternate email add/edit dialog -->
		<app-dialog
			v-model="dialog.email_alt.show"
			max-width="320"
			:title="dialog.email_alt.new ? 'New Email' : 'Update Email'"
		>
			<app-dialog-body>
				<input-text type="email" v-model="dialog.email_alt.value" label="Email" />
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					:color="dialog.email_alt.new ? 'success' : 'primary'"
					@click="editResource('email_alt', 'Email')"
				>
					{{ dialog.email_alt.new ? 'Add' : 'Update' }}
				</app-button>
				<app-button @click="dialog.email_alt.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
export default {
	async created() {
		this.$store.dispatch('app/startLoading')
		try {
			if (this.id !== 'new') {
				this.realtor = await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('realtors')
					.to(this.id)
					.get()

				this.syncResourcesTables()
			}
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}
		this.$store.dispatch('app/stopLoading')
	},
	data() {
		return {
			realtor: {
				name: '',
				email: '',
				phone: '',
				office_phone: '',
				brokerage: '',
				avatar: '',
				years_experience: '',
				subscribed: true,
				aliases: [],
				email_alt: [],
			},
			dialog: {
				aliases: {
					show: false,
					position: -1,
					new: false,
					value: '',
				},
				email_alt: {
					show: false,
					position: -1,
					new: false,
					value: '',
				},
			},
			aliasesHeader: [{
				text: 'Alias',
				value: 'value',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				sortable: false,
				align: 'end',
			}],
			altEmailsHeader: [{
				text: 'Email',
				value: 'value',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				sortable: false,
				align: 'end',
			}],
			aliases: [],
			email_alt: [],
			errors: {},
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
		id() {
			return this.$route.params.realtor
		},
		isNew() {
			return this.id === 'new'
		},
	},
	methods: {
		onAddResource(type) {
			this.dialog[type].position = -1
			this.dialog[type].new = true
			this.dialog[type].value = ''
			this.dialog[type].show = true
		},

		onEditResource(type, { id, value }) {
			this.dialog[type].position = id
			this.dialog[type].new = false
			this.dialog[type].value = value
			this.dialog[type].show = true
		},

		onDeleteResource(type, id) {
			this.realtor[type].splice(id, 1)
			this.syncResourcesTable(type)
		},

		syncResourcesTable(type) {
			this[type] = this.realtor[type].map((value, i) => ({
				id: i,
				value,
			}))
		},

		syncResourcesTables() {
			this.syncResourcesTable('aliases')
			this.syncResourcesTable('email_alt')
		},

		editResource(type, name) {
			const { new: isNew, position, value } = this.dialog[type]

			if (value === '') {
				this.$notifications.pushAlert('error', `${name} is required`)
				return
			}
			if (isNew) {
				this.realtor[type].push(value)
			} else {
				this.realtor[type][position] = value
			}

			this.syncResourcesTable(type)
			this.dialog[type].show = false
		},

		async onSubmit() {
			this.$store.dispatch('app/startLoading')

			const request = this.$ravello.request()
				.v(2)
				.to('markets')
				.to(this.marketId)
				.to('realtors')

			const payload = {
				name: this.realtor.name,
				email: this.realtor.email,
				phone: this.realtor.phone,
				office_phone: this.realtor.office_phone,
				brokerage: this.realtor.brokerage,
				avatar: this.realtor.avatar,
				years_experience: this.realtor.years_experience,
				subscribed: this.realtor.subscribed,
				aliases: this.realtor.aliases,
				email_alt: this.realtor.email_alt,
			}

			try {
				if (this.isNew) {
					await request.create(payload)
				} else {
					await request
						.to(this.id)
						.update(payload)
				}
				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', `Realtor successfully ${this.isNew ? 'added' : 'updated'}`)
				this.$router.push({ name: 'market-realtors' })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},
	},
}
</script>
